import { useState, useEffect } from "react";
import { X, Car, Motorcycle, Truck, CheckCircle, ArrowRight, ArrowLeft, Sparkle, Warning, Clock, Fire, Phone } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { isLimitedQuotaWindow, SUPPORT_PHONE, SUPPORT_PHONE_TEL } from "@/lib/enrollmentCycle";

const ICON_MAP = { Car, Motorcycle, Truck };

const CALC_STEPS = [
  "Kurs bilgileri kontrol ediliyor...",
  "Mevcut kontenjan sorgulanıyor...",
  "Geçiş ve indirim seçenekleri taranıyor...",
  "Size özel fiyat hesaplanıyor...",
];

// Days until the 8th of the month (online kayıt cycle cutoff, minimum 1)
function daysUntilClose() {
  const now = new Date();
  let target = new Date(now.getFullYear(), now.getMonth(), 8);
  if (now.getDate() >= 8) {
    target = new Date(now.getFullYear(), now.getMonth() + 1, 8);
  }
  return Math.max(1, Math.ceil((target - now) / (1000 * 60 * 60 * 24)));
}


function ClassCard({ course, selected, onClick, badge }) {
  const Icon = ICON_MAP[course.icon] || Car;
  return (
    <button
      data-testid={`class-select-${course.code}`}
      onClick={onClick}
      className={`flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all w-full
        ${selected ? "border-brand-primary bg-orange-50" : "border-stone-200 bg-white hover:border-orange-200"}`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors
        ${selected ? "bg-brand-primary text-white" : "bg-stone-100 text-stone-500"}`}>
        <Icon size={20} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-sm text-brand-ink">{course.name}</div>
        <div className="text-xs text-stone-500 truncate">{course.description?.slice(0, 55)}</div>
      </div>
      {badge && (
        <span className="text-[10px] bg-blue-100 text-blue-700 font-bold px-2 py-0.5 rounded-full shrink-0">
          {badge}
        </span>
      )}
      {selected && <CheckCircle size={18} weight="fill" className="text-brand-primary shrink-0" />}
    </button>
  );
}

// Steps: 1=target class, 2=has license, 2b=existing class (if yes), 3=contact, 4=price
export default function PriceAdvisorModal({ open, onClose, defaultClass = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [upgradePaths, setUpgradePaths] = useState([]);
  const [step, setStep] = useState(1);
  const [targetClass, setTargetClass] = useState(null);
  const [hasLicense, setHasLicense] = useState(null);
  const [existingClass, setExistingClass] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "" });
  const [price, setPrice] = useState(null);
  const [upgradeName, setUpgradeName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [calcStep, setCalcStep] = useState(0);
  const [showLaterWarning, setShowLaterWarning] = useState(false);

  useEffect(() => {
    api.get("/courses").then(r => setCourses(r.data)).catch(() => {});
    api.get("/upgrade-paths").then(r => setUpgradePaths(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    setError(""); setHasLicense(null); setExistingClass(null); setPrice(null); setUpgradeName("");
    if (defaultClass) { setTargetClass(defaultClass); setStep(2); }
    else { setTargetClass(null); setStep(1); }
    if (user) setForm({ full_name: user.full_name || "", email: user.email || "", phone: user.phone || "" });
    else setForm({ full_name: "", email: "", phone: "" });
  }, [open, defaultClass, user]);

  if (!open) return null;

  const targetCourse = courses.find(c => c.code === targetClass);

  // For step 2b: find which "from" classes have a defined upgrade path TO the target class
  const availableFromCourses = courses.filter(c =>
    c.code !== targetClass &&
    upgradePaths.some(p => p.from_class === c.code && p.to_class === targetClass)
  );

  // Find the specific upgrade path for from→to
  const findUpgradePath = (from, to) =>
    upgradePaths.find(p => p.from_class === from && p.to_class === to);

  const totalSteps = hasLicense === "yes" ? 4 : 3;
  const stepDisplay = { 1: 1, 2: 2, "2b": 3, 3: hasLicense === "yes" ? 4 : 3, 4: null };
  const currentProgress = stepDisplay[step] || totalSteps;

  async function submit() {
    setLoading(true); setError(""); setCalcStep(0);
    try {
      let calculatedPrice, resolvedName;
      if (hasLicense === "yes" && existingClass) {
        const path = findUpgradePath(existingClass, targetClass);
        calculatedPrice = path?.price ?? targetCourse?.price_additional ?? targetCourse?.price ?? 0;
        resolvedName = path?.name ?? `${existingClass}'den ${targetClass} Sınıfı`;
      } else {
        calculatedPrice = targetCourse?.price_first ?? targetCourse?.price ?? 0;
        resolvedName = `${targetCourse?.name} — İlk Ehliyet`;
      }
      setPrice(calculatedPrice);
      setUpgradeName(resolvedName);

      // Fire API call in background
      api.post("/price-leads", {
        course_class: targetClass,
        has_license: hasLicense === "yes",
        existing_class: hasLicense === "yes" ? existingClass : null,
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      }).catch(() => {});

      // Animate calc steps: 4 steps × ~850ms = ~3.4s
      for (let i = 0; i < CALC_STEPS.length; i++) {
        await new Promise(r => setTimeout(r, i === 0 ? 0 : 850));
        setCalcStep(i);
      }
      await new Promise(r => setTimeout(r, 950));
      setStep(4);
    } catch {
      setError("Bir hata oluştu, lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden animate-slide-up" style={{position:"relative"}}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-stone-100">
          <div>
            <div className="font-heading font-bold text-lg text-brand-ink">Fiyat Danışmanı</div>
            <div className="text-xs text-stone-500 mt-0.5">Size özel fiyat — 1 dakikada</div>
          </div>
          <button onClick={onClose} data-testid="modal-close"
            className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition-colors">
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Progress */}
        {step !== 4 && (
          <div className="px-6 pt-4">
            <div className="flex gap-1.5">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div key={i} className={`flex-1 h-1 rounded-full transition-all duration-300
                  ${i < currentProgress ? "bg-brand-primary" : "bg-stone-200"}`} />
              ))}
            </div>
            <div className="text-[11px] text-stone-400 mt-1.5">Adım {currentProgress} / {totalSteps}</div>
          </div>
        )}

        <div className="px-6 pb-6 pt-4 max-h-[72vh] overflow-y-auto">

          {/* STEP 1: Almak istediğiniz sınıf */}
          {step === 1 && (
            <div>
              <h2 className="font-heading font-bold text-xl text-brand-ink mb-1">Hangi ehliyet sınıfını almak istiyorsunuz?</h2>
              <p className="text-sm text-stone-500 mb-4">Tüm sınıflarımız listeleniyor.</p>
              <div className="space-y-2.5">
                {courses.length === 0 && <div className="text-center py-8 text-stone-400 text-sm">Yükleniyor...</div>}
                {courses.map(c => (
                  <ClassCard key={c.code} course={c} selected={targetClass === c.code}
                    onClick={() => setTargetClass(c.code)} />
                ))}
              </div>
              <button data-testid="step1-next" disabled={!targetClass} onClick={() => setStep(2)}
                className="mt-5 w-full py-3.5 rounded-2xl font-bold text-sm bg-brand-ink text-white disabled:opacity-40 hover:bg-brand-inkLight transition-colors flex items-center justify-center gap-2">
                Devam Et <ArrowRight weight="bold" size={15} />
              </button>
            </div>
          )}

          {/* STEP 2: Mevcut ehliyet var mı? */}
          {step === 2 && (
            <div>
              <button onClick={() => setStep(1)}
                className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-brand-ink mb-4 transition-colors">
                <ArrowLeft size={14} weight="bold" /> Geri
              </button>
              <h2 className="font-heading font-bold text-xl text-brand-ink mb-1">Daha önce ehliyet aldınız mı?</h2>
              <div className="mb-4 px-3 py-2 bg-orange-50 rounded-xl text-sm text-stone-600">
                Hedef: <span className="font-bold text-brand-primary">{targetCourse?.name}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { val: "no",  label: "Hayır", sub: "Bu benim ilk ehliyetim" },
                  { val: "yes", label: "Evet",  sub: "Başka bir sınıfım var" },
                ].map(opt => (
                  <button key={opt.val} data-testid={`license-${opt.val}`}
                    onClick={() => setHasLicense(opt.val)}
                    className={`p-5 rounded-2xl border-2 text-center transition-all
                      ${hasLicense === opt.val ? "border-brand-primary bg-orange-50" : "border-stone-200 hover:border-orange-200"}`}>
                    <div className="font-heading font-bold text-lg text-brand-ink">{opt.label}</div>
                    <div className="text-xs text-stone-500 mt-1">{opt.sub}</div>
                    {hasLicense === opt.val && <CheckCircle size={18} weight="fill" className="text-brand-primary mx-auto mt-2" />}
                  </button>
                ))}
              </div>
              <button data-testid="step2-next" disabled={hasLicense === null}
                onClick={() => hasLicense === "yes" ? setStep("2b") : setStep(3)}
                className="mt-5 w-full py-3.5 rounded-2xl font-bold text-sm bg-brand-ink text-white disabled:opacity-40 hover:bg-brand-inkLight transition-colors flex items-center justify-center gap-2">
                Devam Et <ArrowRight weight="bold" size={15} />
              </button>
            </div>
          )}

          {/* STEP 2b: Mevcut hangi sınıfa sahipsiniz? */}
          {step === "2b" && (
            <div>
              <button onClick={() => setStep(2)}
                className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-brand-ink mb-4 transition-colors">
                <ArrowLeft size={14} weight="bold" /> Geri
              </button>
              <h2 className="font-heading font-bold text-xl text-brand-ink mb-1">Hangi sınıf ehliyete sahipsiniz?</h2>
              <p className="text-sm text-stone-500 mb-4">
                <span className="font-semibold text-brand-primary">{targetCourse?.name}</span> için geçiş fiyatını hesaplayalım.
              </p>

              {availableFromCourses.length > 0 ? (
                <div className="space-y-2.5">
                  {availableFromCourses.map(c => {
                    const path = findUpgradePath(c.code, targetClass);
                    return (
                      <ClassCard key={c.code} course={c}
                        selected={existingClass === c.code}
                        onClick={() => setExistingClass(c.code)}
                        badge={path ? `${path.price.toLocaleString("tr-TR")}₺` : null}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-2xl text-sm text-stone-600">
                  <Warning size={18} className="text-yellow-500 shrink-0 mt-0.5"/>
                  <div>Bu sınıf için tanımlı geçiş yolu bulunamadı. Lütfen bizi arayın veya formu doldurun.</div>
                </div>
              )}

              <button data-testid="step2b-next"
                disabled={availableFromCourses.length > 0 && !existingClass}
                onClick={() => setStep(3)}
                className="mt-5 w-full py-3.5 rounded-2xl font-bold text-sm bg-brand-ink text-white disabled:opacity-40 hover:bg-brand-inkLight transition-colors flex items-center justify-center gap-2">
                Devam Et <ArrowRight weight="bold" size={15} />
              </button>
            </div>
          )}

          {/* STEP 3: İletişim bilgileri */}
          {step === 3 && (
            <div>
              <button onClick={() => hasLicense === "yes" ? setStep("2b") : setStep(2)}
                className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-brand-ink mb-4 transition-colors">
                <ArrowLeft size={14} weight="bold" /> Geri
              </button>
              <h2 className="font-heading font-bold text-xl text-brand-ink mb-1">Size özel fiyatı hesaplayalım</h2>

              {/* Summary pill */}
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-xl text-sm flex items-center gap-2 flex-wrap">
                <span className="font-bold text-brand-primary">{targetCourse?.name}</span>
                {hasLicense === "yes" && existingClass ? (
                  <>
                    <span className="text-stone-400">·</span>
                    <span className="text-stone-600">
                      {findUpgradePath(existingClass, targetClass)?.name ?? `${existingClass}→${targetClass} Geçiş`}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-stone-400">·</span>
                    <span className="text-stone-600">İlk ehliyet</span>
                  </>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Ad Soyad</label>
                  <input data-testid="lead-fullname" value={form.full_name}
                    onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                    placeholder="Ahmet Yılmaz"
                    className="mt-1.5 w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-primary transition-colors" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-stone-500 uppercase tracking-wider">E-posta</label>
                  <input data-testid="lead-email" type="email" value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="ahmet@ornek.com"
                    className="mt-1.5 w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-primary transition-colors" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Telefon</label>
                  <input data-testid="lead-phone" type="tel" value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="0532 000 00 00"
                    className="mt-1.5 w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-primary transition-colors" />
                </div>
              </div>
              {error && <div className="mt-3 text-red-600 text-sm">{error}</div>}
              <p className="text-[11px] text-stone-400 mt-3">Bilgileriniz yalnızca fiyat bildirimi amacıyla kullanılır.</p>
              <button data-testid="step3-submit"
                disabled={loading || !form.full_name.trim() || !form.email.trim() || !form.phone.trim()}
                onClick={submit}
                className="mt-4 w-full py-3.5 rounded-2xl font-bold text-sm bg-brand-primary text-white disabled:opacity-40 hover:opacity-90 transition-opacity flex items-center justify-center gap-2">
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    {CALC_STEPS[calcStep]}
                  </span>
                ) : (
                  <><Sparkle weight="fill" size={15} /> Fiyatı Göster</>
                )}
              </button>
            </div>
          )}

          {/* CALCULATING ANIMATION (overlay while loading between step 3→4) */}
          {loading && step === 3 && (
            <div className="absolute inset-0 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center z-10 rounded-3xl px-8">
              {/* Animated rings */}
              <div className="relative w-20 h-20 mb-6">
                <div className="absolute inset-0 rounded-full border-4 border-brand-primary/20 animate-ping" />
                <div className="absolute inset-1 rounded-full border-4 border-brand-primary/40" style={{animation: "spin 1.2s linear infinite"}} />
                <div className="absolute inset-3 rounded-full bg-orange-50 flex items-center justify-center">
                  <Sparkle size={20} weight="fill" className="text-brand-primary" />
                </div>
              </div>

              <div className="font-heading font-bold text-xl text-brand-ink mb-2 text-center">Fiyatınız Hesaplanıyor</div>
              <div className="text-sm text-stone-500 text-center mb-6">Lütfen bekleyin...</div>

              {/* Step indicators */}
              <div className="w-full max-w-xs space-y-2.5">
                {CALC_STEPS.map((txt, i) => (
                  <div key={i} className={`flex items-center gap-3 transition-all duration-500 ${i <= calcStep ? "opacity-100" : "opacity-25"}`}>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-all duration-300
                      ${i < calcStep ? "bg-green-500" : i === calcStep ? "bg-brand-primary" : "bg-stone-200"}`}>
                      {i < calcStep
                        ? <CheckCircle size={12} weight="fill" className="text-white" />
                        : i === calcStep
                          ? <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                          : <span className="w-2 h-2 rounded-full bg-stone-400" />
                      }
                    </div>
                    <span className={`text-xs font-medium ${i === calcStep ? "text-brand-ink" : i < calcStep ? "text-stone-500" : "text-stone-300"}`}>
                      {txt}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 4: Fiyat sonucu */}
          {step === 4 && (
            <div className="text-center pt-2">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={28} weight="fill" className="text-green-500" />
              </div>
              <h2 className="font-heading font-bold text-xl text-brand-ink">{upgradeName}</h2>
              <p className="text-sm text-stone-500 mt-1 mb-5">KDV dahil · Tüm sınıf kursları için geçerli</p>

              <div className="bg-gradient-to-br from-orange-50 to-white border-2 border-brand-primary rounded-3xl p-7 mb-5 text-left">
                <div className="text-[11px] uppercase tracking-widest text-orange-400 font-semibold mb-2 text-center">Size Özel Fiyat</div>
                <div className="flex items-end justify-center gap-1 mb-5">
                  <div className="font-heading font-black text-6xl text-brand-primary tracking-tighter leading-none">
                    {price?.toLocaleString("tr-TR")}
                  </div>
                  <div className="font-heading font-bold text-3xl text-brand-ink pb-1">₺</div>
                </div>
                <div className="space-y-2">
                  {[
                    "Sertifika ücreti dahil",
                    "Ek ve gizli ücret yok",
                    "Vade farklı veya vade farksız 9 taksite kadar taksit imkanı",
                    "Elden veya kredi kartına taksit imkanı",
                    "MEB onaylı eğitim materyalleri dahil",
                  ].map(t => (
                    <div key={t} className="flex items-start gap-2.5 text-sm text-stone-700">
                      <CheckCircle weight="fill" size={15} className="text-green-500 shrink-0 mt-0.5" />{t}
                    </div>
                  ))}
                </div>
              </div>

              {isLimitedQuotaWindow() && (
                <div className="rounded-2xl border-2 border-red-300 bg-red-50 p-4 mb-3 text-left animate-fade-in" data-testid="limited-quota-banner">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Fire size={18} weight="fill" className="text-red-500 shrink-0" />
                    <span className="font-bold text-sm text-brand-ink">Sınırlı Kontenjan!</span>
                  </div>
                  <p className="text-xs text-stone-600 mb-3">Bu dönem online kayıtlarımız şu an değerlendirme aşamasında. Yerinizi kaçırmamak için hemen bizi arayın, size özel yardımcı olalım.</p>
                  <a href={`tel:${SUPPORT_PHONE_TEL}`} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm bg-red-500 text-white hover:bg-red-600 transition-colors" data-testid="limited-quota-call-btn">
                    <Phone weight="fill" size={15} /> {SUPPORT_PHONE}
                  </a>
                </div>
              )}

              <button data-testid="price-enroll-btn"
                onClick={() => { onClose(); navigate("/basla"); }}
                className="w-full py-4 rounded-2xl font-bold text-base bg-brand-ink text-white hover:bg-brand-inkLight transition-colors flex items-center justify-center gap-2 mb-3">
                Hemen Kayıt Ol <ArrowRight weight="bold" size={16} />
              </button>

              {/* Later button with urgency */}
              {!showLaterWarning ? (
                <button
                  data-testid="price-later-btn"
                  onClick={() => setShowLaterWarning(true)}
                  className="w-full py-3 rounded-2xl font-semibold text-sm border border-stone-200 text-stone-500 hover:bg-stone-50 transition-colors">
                  Daha sonra karar vereceğim
                </button>
              ) : (
                <div className="rounded-2xl border-2 border-orange-300 bg-orange-50 p-4 text-left animate-fade-in">
                  <div className="flex items-center gap-2 mb-2">
                    <Fire size={18} weight="fill" className="text-orange-500 shrink-0" />
                    <span className="font-bold text-sm text-brand-ink">Beklemek riskli olabilir!</span>
                  </div>
                  <div className="text-xs text-stone-600 space-y-1.5 mb-3">
                    <div className="flex items-center gap-1.5">
                      <Clock size={13} className="text-orange-400 shrink-0" />
                      {isLimitedQuotaWindow() ? (
                        <span>Online kayıtlar <strong className="text-red-500">değerlendirme aşamasında</strong> — telefonla yerinizi ayırtın</span>
                      ) : (
                        <span>Bu dönem kaydı <strong className="text-brand-primary">{daysUntilClose()} gün</strong> sonra kapanıyor</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Warning size={13} className="text-orange-400 shrink-0" />
                      <span>Kontenjan sınırlı — yerinizi şimdiden ayırtın</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle size={13} weight="fill" className="text-green-500 shrink-0" />
                      <span>Fiyatınız e-postanıza gönderildi, istediğinizde geri dönebilirsiniz</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { onClose(); navigate("/basla"); }}
                      className="flex-1 py-2.5 rounded-xl font-bold text-xs bg-brand-primary text-white hover:opacity-90 transition-opacity">
                      Yerimizi Ayırt
                    </button>
                    <button
                      onClick={onClose}
                      className="flex-1 py-2.5 rounded-xl font-semibold text-xs border border-stone-300 text-stone-500 hover:bg-stone-100 transition-colors">
                      Tamam, sonra dönücem
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
